/**
 * New Mission Chat Dialog
 * Opens from "+ New" button in Missions section
 * Shows list of available missions user can participate in
 * Fetches missions from orchestration-service API
 */

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  ClipboardList,
  Users,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Button } from '@agion/ui'
import { useMissionStore } from '../../hooks/useMissions'

interface NewMissionChatDialogProps {
  isOpen: boolean
  onClose: () => void
}

export const NewMissionChatDialog: React.FC<NewMissionChatDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation(['chat'])
  const navigate = useNavigate()
  const { missions, loadMissions, isLoading } = useMissionStore()

  // Load missions when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      loadMissions()
    }
  }, [isOpen, loadMissions])

  // F9 (chat-ui catch-up): dropped the agents-from-requires fallback that
  // depended on the deleted direct-agent invocation store.
  // mission.participants is the single source of truth here.
  const missionsWithAgents = React.useMemo(() => {
    return missions.map(mission => {
      const agentParticipants = mission.participants
        ?.filter(p => p.actorType === 'agent')
        .map(p => p.displayName) || []

      return {
        id: mission.id,
        name: mission.name || mission.title || 'Unnamed Mission',
        description: mission.description,
        status: mission.status || mission.state,
        agents: agentParticipants,
        // F11: hasExistingSession was based on the dead chat_sessions table.
        // The mission-centric model treats every navigation as either a fresh
        // execution or resuming whichever execution the user picks from the
        // sidebar.
        hasExistingSession: false,
      }
    })
  }, [missions])

  const handleSelectMission = (missionId: string, _missionName: string) => {
    // F11: chat_sessions table is unwritten in the mission-centric model.
    // Navigate straight to the mission page; the sidebar+conversation history
    // reads execution state from /v1/executions.
    navigate(`/mission/${missionId}`)
    onClose()
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <div
              className="bg-chat-surface rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-chat-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-chat-accent-600" />
                  <h2 className="text-lg font-semibold text-chat-gray-900">
                    {t('chat:newMissionChat', 'New Mission Chat')}
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                >
                  <X className="w-5 h-5 text-chat-gray-500" />
                </Button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(80vh-80px)]">
                {isLoading ? (
                  <div className="px-6 py-12 text-center">
                    <div className="animate-spin h-8 w-8 border-2 border-chat-accent-600 border-t-transparent rounded-full mx-auto mb-3"></div>
                    <p className="text-chat-gray-500">
                      {t('chat:loadingMissions', 'Loading missions...')}
                    </p>
                  </div>
                ) : missionsWithAgents.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <ClipboardList className="w-12 h-12 text-chat-gray-300 mx-auto mb-3" />
                    <p className="text-chat-gray-500">
                      {t('chat:noMissionsAvailable', 'No missions available')}
                    </p>
                    <p className="text-sm text-chat-gray-400 mt-1">
                      {t(
                        'chat:noMissionsDescription',
                        'Create a mission in the admin panel to get started'
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="p-6 space-y-3">
                    {missionsWithAgents.map((mission) => (
                      <Button
                        key={mission.id}
                        variant="ghost"
                        onClick={() => handleSelectMission(mission.id, mission.name)}
                        className="w-full h-auto text-left px-4 py-3 rounded-lg border border-chat-gray-200 hover:border-chat-accent-300 hover:bg-chat-accent-50 transition-all group"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-chat-accent-100 group-hover:bg-chat-accent-200 flex items-center justify-center flex-shrink-0">
                            <ClipboardList className="w-5 h-5 text-chat-accent-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-chat-gray-900">
                                {mission.name}
                              </h3>
                              {mission.hasExistingSession && (
                                <span className="text-xs px-1.5 py-0.5 bg-chat-green-100 text-chat-green-700 rounded">
                                  {t('chat:hasChat', 'Has chat')}
                                </span>
                              )}
                              {mission.status && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  mission.status === 'active'
                                    ? 'bg-chat-blue-100 text-chat-blue-700'
                                    : mission.status === 'archived'
                                    ? 'bg-chat-gray-100 text-chat-gray-600'
                                    : mission.status === 'inactive'
                                    ? 'bg-chat-amber-100 text-chat-amber-700'
                                    : 'bg-chat-gray-100 text-chat-gray-600'
                                }`}>
                                  {mission.status}
                                </span>
                              )}
                            </div>
                            {mission.description && (
                              <p className="text-sm text-chat-gray-500 mb-1.5 line-clamp-2">
                                {mission.description}
                              </p>
                            )}
                            <div className="flex items-center gap-2 text-sm text-chat-gray-500">
                              <Users className="w-4 h-4" />
                              <span>
                                {mission.agents.length} {mission.agents.length === 1 ? 'agent' : 'agents'}
                              </span>
                            </div>
                            {mission.agents.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {mission.agents.slice(0, 3).map((agentName, idx) => (
                                  <span
                                    key={idx}
                                    className="text-xs px-2 py-0.5 bg-chat-gray-100 group-hover:bg-white rounded text-chat-gray-600"
                                  >
                                    {agentName}
                                  </span>
                                ))}
                                {mission.agents.length > 3 && (
                                  <span className="text-xs px-2 py-0.5 bg-chat-gray-100 group-hover:bg-white rounded text-chat-gray-600">
                                    +{mission.agents.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-chat-gray-200 flex justify-end">
                <Button
                  variant="ghost"
                  onClick={onClose}
                >
                  {t('common:cancel', 'Cancel')}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default NewMissionChatDialog
